export type EntityGenerateInputs = {
  description?: string;
  role?: string;
  roleLabel?: string;
  roleExplicitlySelected?: boolean;
};

export type EntityGenerateCtaActionInputs = EntityGenerateInputs & {
  customInstructions?: string;
};

function formatRoleKey(role: string): string {
  return role
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function entityGenerateTypeLabel(
  inputs: Pick<EntityGenerateInputs, "role" | "roleLabel" | "roleExplicitlySelected">,
): string | null {
  if (inputs.roleExplicitlySelected !== true) return null;
  const label = inputs.roleLabel?.trim() || (inputs.role?.trim() ? formatRoleKey(inputs.role) : "");
  return label || null;
}

export function entityGenerateInputsFromCtaAction(
  ctaAction: EntityGenerateCtaActionInputs,
): EntityGenerateInputs | null {
  const description = (ctaAction.description ?? ctaAction.customInstructions)?.trim();
  const role = ctaAction.role?.trim();
  const roleLabel = ctaAction.roleLabel?.trim();
  const hasType = ctaAction.roleExplicitlySelected === true && !!(roleLabel || role);
  if (!description && !hasType) return null;
  return {
    ...(description ? { description } : {}),
    ...(role ? { role } : {}),
    ...(roleLabel ? { roleLabel } : {}),
    ...(ctaAction.roleExplicitlySelected ? { roleExplicitlySelected: true } : {}),
  };
}

/** Persisted user bubble text — type/description live in thinking-step metadata only. */
export function buildEntityGenerateUserMessageContent(
  baseTitle: string,
  _inputs?: EntityGenerateInputs,
): string {
  return baseTitle.trim();
}

/** Collapse legacy multi-line entity-generate bubbles to the title line for display. */
export function entityGenerateUserMessageDisplayContent(
  content: string,
  metadata?: { ctaActionType?: string; generateInputs?: unknown } | null,
): string {
  const ctaType = metadata?.ctaActionType;
  const isEntityGenerate =
    ctaType === "generate_character"
    || ctaType === "generate_worldbuilding"
    || metadata?.generateInputs != null;
  if (!isEntityGenerate) return content;

  const firstLine = content.split("\n")[0]?.trim();
  if (firstLine) return firstLine;
  if (ctaType === "generate_character") return "Generate Character";
  if (ctaType === "generate_worldbuilding") return "Generate Element";
  return content;
}

export function buildEntityGenerateUserMessageFromCtaAction(
  baseTitle: string,
  ctaAction: EntityGenerateCtaActionInputs,
): string {
  return buildEntityGenerateUserMessageContent(baseTitle, {
    description: ctaAction.description ?? ctaAction.customInstructions,
    role: ctaAction.role,
    roleLabel: ctaAction.roleLabel,
    roleExplicitlySelected: ctaAction.roleExplicitlySelected,
  });
}
